import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TrendingUp, TrendingDown, DollarSign, Cpu, BarChart3,
  Calendar, ArrowUpRight, ArrowDownRight, Zap, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;
const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat("id-ID").format(v || 0);
const PIE_COLORS = ["#10b981", "#3b82f6", "#a855f7", "#f59e0b", "#6b7280"];

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="rounded-md border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color || "bg-secondary"}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-heading text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminFinance() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/finance`, { params, withCredentials: true });
      setData(res.data);
    } catch { } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const setPreset = (days) => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    setDateFrom(from.toISOString().split("T")[0]);
    setDateTo(now.toISOString().split("T")[0]);
  };

  if (loading && !data) {
    return (
      <div className="p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold mb-6">{t("admin.finance.title")}</h1>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-md border border-border bg-card animate-pulse" />)}
        </div>
      </div>
    );
  }

  const revenue = data?.revenue?.total || 0;
  const cogs = data?.cogs?.total || 0;
  const geminiCost = data?.cogs?.gemini_cost || 0;
  const hiveCost = data?.cogs?.hive_cost || 0;
  const hiveAnalyses = data?.cogs?.hive_analyses || 0;
  const profit = data?.profitability?.gross_profit || 0;
  const marginPct = data?.profitability?.gross_margin_pct || 0;
  const isProfit = profit >= 0;

  const typeData = Object.entries(data?.revenue?.by_type || {}).map(([k, v]) => ({
    name: k === "subscription" ? "Subscription"
        : k === "recurring_subscription" ? "Recurring Sub"
        : k === "credits" ? "Credits"
        : k === "auto_recharge" ? "Auto-Recharge"
        : k === "trial" ? "Trial"
        : k,
    value: v.revenue,
    count: v.count,
  }));

  const planData = Object.entries(data?.revenue?.by_plan || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    value: v.revenue,
    count: v.count,
  }));

  // AI cost breakdown for pie chart
  const aiCostData = [
    ...(geminiCost > 0 ? [{ name: "Gemini AI", value: geminiCost }] : []),
    ...(hiveCost > 0   ? [{ name: "Hive AI",   value: hiveCost   }] : []),
  ];
  const AI_PIE_COLORS = ["#14b8a6", "#f59e0b"];

  return (
    <div className="p-6 md:p-8" data-testid="admin-finance-page">
      {/* Header */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("admin.finance.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("admin.finance.period")}: {data?.period?.from || "—"} — {data?.period?.to || "—"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[7, 30, 90].map(d => (
            <Button key={d} size="sm" variant="outline" className="rounded-sm text-xs h-7"
              onClick={() => setPreset(d)} data-testid={`preset-${d}d`}>
              {d}d
            </Button>
          ))}
          <div className="flex items-center gap-1.5">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="h-7 rounded-sm text-xs font-mono w-32" data-testid="finance-date-from" />
            <span className="text-xs text-muted-foreground">—</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="h-7 rounded-sm text-xs font-mono w-32" data-testid="finance-date-to" />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={DollarSign} label={t("admin.finance.revenue")} value={fmt(revenue)}
          sub={`${data?.revenue?.transactions || 0} ${t("admin.finance.transactions")}`}
          color="bg-verdict-real/10 text-verdict-real" testId="stat-revenue" />
        <StatCard icon={Cpu} label={t("admin.finance.cogs")} value={fmt(cogs)}
          sub={`${data?.cogs?.analyses || 0} ${t("admin.finance.analyses")}`}
          color="bg-verdict-fake/10 text-verdict-fake" testId="stat-cogs" />
        <StatCard icon={isProfit ? TrendingUp : TrendingDown} label={t("admin.finance.grossProfit")}
          value={fmt(Math.abs(profit))}
          sub={`${t("admin.finance.margin")}: ${marginPct}%`}
          color={isProfit ? "bg-verdict-real/10 text-verdict-real" : "bg-verdict-fake/10 text-verdict-fake"}
          testId="stat-profit" />
        <StatCard icon={BarChart3} label={t("admin.finance.today")}
          value={`${data?.today?.analyses || 0} ${t("admin.finance.analyses")}`}
          sub={`${t("admin.finance.cogs")}: ${fmt(data?.today?.cost || 0)}`}
          color="bg-brand-primary/10 text-brand-primary" testId="stat-today" />
      </div>

      {/* P/L Status Banner */}
      <div className={`rounded-md border p-4 mb-8 flex items-center gap-3 ${
        isProfit ? "border-verdict-real/30 bg-verdict-real/5" : "border-verdict-fake/30 bg-verdict-fake/5"
      }`} data-testid="pl-banner">
        {isProfit ? <ArrowUpRight className="w-5 h-5 text-verdict-real" /> : <ArrowDownRight className="w-5 h-5 text-verdict-fake" />}
        <div>
          <span className={`font-heading font-bold ${isProfit ? "text-verdict-real" : "text-verdict-fake"}`}>
            {isProfit ? t("admin.finance.profitStatus") : t("admin.finance.lossStatus")}
          </span>
          <span className="text-sm text-muted-foreground ml-2">
            {fmt(Math.abs(profit))} ({marginPct}% {t("admin.finance.margin")})
          </span>
        </div>
      </div>

      {/* AI Cost Breakdown */}
      <div className="rounded-md border border-border bg-card p-5 mb-8" data-testid="ai-cost-breakdown">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-brand-primary" />
          <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">AI Cost Breakdown</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Total COGS */}
          <div className="rounded-sm bg-secondary/50 p-4">
            <div className="text-xs text-muted-foreground font-mono mb-1">Total COGS</div>
            <div className="text-xl font-bold font-heading">{fmt(cogs)}</div>
            <div className="text-xs text-muted-foreground mt-1">{fmtNum(data?.cogs?.analyses || 0)} analisis</div>
          </div>
          {/* Gemini AI */}
          <div className="rounded-sm bg-secondary/50 p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-2 h-2 rounded-full bg-teal-500" />
              <span className="text-xs text-muted-foreground font-mono">Gemini AI</span>
            </div>
            <div className="text-xl font-bold font-heading">{fmt(geminiCost)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {cogs > 0 ? ((geminiCost / cogs) * 100).toFixed(1) : 0}% dari total COGS
            </div>
          </div>
          {/* Hive AI */}
          <div className="rounded-sm bg-secondary/50 p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-xs text-muted-foreground font-mono">Hive AI</span>
            </div>
            <div className="text-xl font-bold font-heading">{fmt(hiveCost)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {fmtNum(hiveAnalyses)} analisis dengan Hive
              {cogs > 0 && ` • ${((hiveCost / cogs) * 100).toFixed(1)}% dari COGS`}
            </div>
          </div>
        </div>
        {/* Mini pie chart for AI cost split */}
        {aiCostData.length > 0 && (
          <div className="mt-4 flex items-center gap-4">
            <ResponsiveContainer width={80} height={80}>
              <PieChart>
                <Pie data={aiCostData} dataKey="value" cx="50%" cy="50%" outerRadius={35} innerRadius={20}>
                  {aiCostData.map((_, i) => <Cell key={i} fill={AI_PIE_COLORS[i % AI_PIE_COLORS.length]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5">
              {aiCostData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: AI_PIE_COLORS[i] }} />
                  <span className="font-mono">{d.name}</span>
                  <span className="font-semibold ml-1">{fmt(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Revenue vs Cost Trend */}
        <div className="lg:col-span-2 rounded-md border border-border bg-card p-5">
          <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("admin.finance.trend")}</h3>
          {data?.daily_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--muted-foreground)" />
                <YAxis tick={{ fontSize: 10 }} stroke="var(--muted-foreground)" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmt(v)} labelStyle={{ color: "var(--foreground)" }} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} name={t("admin.finance.revenue")} />
                <Line type="monotone" dataKey="cost" stroke="#ef4444" strokeWidth={2} dot={false} name={t("admin.finance.cogs")} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-sm text-muted-foreground">{t("admin.finance.noData")}</div>
          )}
        </div>

        {/* Revenue by Type */}
        <div className="rounded-md border border-border bg-card p-5">
          <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("admin.finance.byType")}</h3>
          {typeData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                    {typeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {typeData.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="font-mono">{d.name}</span>
                    </div>
                    <span className="font-mono font-semibold">{fmt(d.value)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">{t("admin.finance.noData")}</div>
          )}
        </div>
      </div>

      {/* Revenue by Plan */}
      {planData.length > 0 && (
        <div className="rounded-md border border-border bg-card p-5 mb-8">
          <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("admin.finance.byPlan")}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={planData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
              <YAxis tick={{ fontSize: 10 }} stroke="var(--muted-foreground)" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12 }} />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} name={t("admin.finance.revenue")} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
